/**
 * nodesFromLinks - Creates nodes array from links-only data
 *
 * Useful when data only contains links (like flare-dependencies.json from vega-datasets)
 * and nodes need to be inferred from source/target references.
 *
 * @param {Array<Object>} links - Array of link objects with source/target
 * @param {Object} options - Configuration options
 * @param {function} [options.linkSource] - Extract source from link (default: d => d.source)
 * @param {function} [options.linkTarget] - Extract target from link (default: d => d.target)
 * @param {function} [options.nodeId] - Create node ID from extracted value (default: identity)
 * @param {function} [options.nodeProps] - Additional node properties (receives id, returns object)
 * @returns {Object} { nodes: [...], links: [...] } - Ready for ForceGraph
 *
 * @example
 * // Edge-list only data (like flare-dependencies.json)
 * const edgeData = [{ source: 35, target: 4 }, { source: 190, target: 4 }];
 * const graph = nodesFromLinks(edgeData);
 * // => { nodes: [{ id: 4 }, { id: 35 }, { id: 190 }], links: [...] }
 *
 * @example
 * // With custom node properties
 * const graph = nodesFromLinks(edgeData, {
 *   nodeProps: (id) => ({ id, label: `Node ${id}` })
 * });
 */
export function nodesFromLinks(
  links,
  {
    linkSource = (d) => d.source,
    linkTarget = (d) => d.target,
    nodeId = (v) => v,
    nodeProps = (id) => ({ id }),
  } = {}
) {
  if (!Array.isArray(links)) {
    throw new Error("nodesFromLinks: links must be an array");
  }

  // Extract unique node identifiers from links
  const nodeSet = new Set();
  for (const link of links) {
    const sourceId = nodeId(linkSource(link));
    const targetId = nodeId(linkTarget(link));
    if (sourceId !== undefined && sourceId !== null) nodeSet.add(sourceId);
    if (targetId !== undefined && targetId !== null) nodeSet.add(targetId);
  }

  // Create nodes array with optional properties
  const nodes = Array.from(nodeSet).map(nodeProps);

  return { nodes, links: [...links] };
}
